import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardTitle, Row, Col, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Button, Form } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import DataTableSkeleton from '../../components/DataTableSkeleton';
import Header from '../../layouts/Header';
import Footer from '../../layouts/Footer';
import moment from 'moment';
import {
    getAllTestimonials,
    createTestimonial,
    updateTestimonial,
    deleteTestimonial,
    toggleTestimonialStatus,
    exportTestimonialsToCSV,
    downloadCSV,
    getTestimonialProductId,
    getTestimonialProductName,
} from '../../helper/testimonial_helper';
import { getAllProducts } from '../../helper/product_helper';
import * as Utils from '../../Utils';
import StarRating from '../../components/admin/StarRating';
import { dataTableCustomStyles, dataTablePaginationOptions } from '../../components/admin/dataTableConfig';
import AdminRowActions, { AdminActionButton } from '../../components/admin/AdminRowActions';
import AdminEmptyState from '../../components/admin/AdminEmptyState';

function Testimonial() {
    const user = useSelector(state => state.user);
    const [loading, setLoading] = useState(false);
    const [testimonials, setTestimonials] = useState([]);
    const [filteredTestimonials, setFilteredTestimonials] = useState([]);
    const [searchText, setSearchText] = useState('');
    const [filterStatus, setFilterStatus] = useState('all'); // all, active, inactive
    // Modal states
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create'); // create or edit
    const [currentTestimonial, setCurrentTestimonial] = useState(null);

    // Form states
    const [formData, setFormData] = useState({
        name: '',
        rating: 5,
        title: '',
        review: '',
        isVerified: false,
        isActive: true,
        productId: '',
    });
    const [profileImage, setProfileImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [products, setProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);

    const productsById = React.useMemo(() => {
        const map = {};
        products.forEach((product) => {
            if (product?._id) map[product._id] = product;
        });
        return map;
    }, [products]);

    // DataTable columns configuration
    const columns = [
        {
            name: '#',
            cell: (row, index) => index + 1,
            width: '60px',
            center: true
        },
        {
            name: 'Profile',
            cell: row => (
                <div className="avatar-sm my-2">
                    {row.profileImage ? (
                        <img
                            src={Utils.getImageUrl(row.profileImage)}
                            alt={row.name}
                            className="rounded-circle"
                            style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                            onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/40';
                            }}
                        />
                    ) : (
                        <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                            style={{ width: '40px', height: '40px' }}>
                            {row.name.charAt(0).toUpperCase()}
                        </div>
                    )}
                </div>
            ),
            width: '80px',
            center: true
        },
        {
            name: 'Name',
            selector: row => row.name,
            sortable: true,
            cell: row => (
                <div>
                    <div className="fw-bold">{row.name}</div>
                    {row.isVerified && (
                        <span className="badge bg-info text-white" style={{ fontSize: '10px' }}>
                            <i className="fa fa-check-circle"></i> Verified
                        </span>
                    )}
                </div>
            ),
            width: '150px'
        },
        {
            name: 'Product',
            selector: row => getTestimonialProductName(row, productsById),
            sortable: true,
            cell: row => (
                <span className="small text-muted">
                    {getTestimonialProductName(row, productsById)}
                </span>
            ),
            width: '160px',
            wrap: true,
        },
        {
            name: 'Rating',
            selector: row => row.rating,
            sortable: true,
            cell: row => <StarRating value={row.rating} />,
            width: '140px'
        },
        {
            name: 'Title',
            selector: row => row.title,
            sortable: true,
            wrap: true,
            width: '200px'
        },
        {
            name: 'Review',
            selector: row => row.review,
            cell: row => (
                <div className="text-truncate" style={{ maxWidth: '280px' }} title={row.review}>
                    {row.review?.length > 80 ? `${row.review.substring(0, 80)}…` : row.review}
                </div>
            ),
            grow: 2,
            minWidth: '200px',
            maxWidth: '300px',
        },
        {
            name: 'Status',
            selector: row => row.isActive,
            sortable: true,
            cell: row => (
                <span className={`badge ${row.isActive ? 'bg-success' : 'bg-secondary'}`}>
                    {row.isActive ? 'Active' : 'Inactive'}
                </span>
            ),
            width: '100px',
            center: true
        },
        {
            name: 'Date',
            selector: row => row.createdAt,
            sortable: true,
            cell: row => moment(row.createdAt).format('DD/MM/YYYY'),
            width: '120px'
        },
        {
            name: 'Actions',
            cell: (row) => (
                <AdminRowActions>
                    <AdminActionButton
                        action={row.isActive ? 'hide' : 'show'}
                        label={row.isActive ? 'Deactivate' : 'Activate'}
                        tone={row.isActive ? undefined : 'success'}
                        onClick={() => handleToggleStatus(row._id)}
                    />
                    <AdminActionButton action="edit" onClick={() => handleEdit(row)} />
                    <AdminActionButton action="delete" onClick={() => handleDelete(row._id)} />
                </AdminRowActions>
            ),
            ignoreRowClick: true,
            width: '140px',
            center: true
        }
    ];

    useEffect(() => {
        fetchTestimonials();
    }, []);

    useEffect(() => {
        let cancelled = false;
        const loadProducts = async () => {
            setProductsLoading(true);
            try {
                const res = await getAllProducts({ page: 1, limit: 500 });
                let list = [];
                if (res?.success && Array.isArray(res?.data?.products)) {
                    list = res.data.products;
                } else if (Array.isArray(res?.products)) {
                    list = res.products;
                } else if (Array.isArray(res?.data)) {
                    list = res.data;
                }
                if (!cancelled) setProducts(list);
            } catch (err) {
                console.error('Error loading products for testimonials:', err);
                if (!cancelled) setProducts([]);
            } finally {
                if (!cancelled) setProductsLoading(false);
            }
        };
        loadProducts();
        return () => {
            cancelled = true;
        };
    }, []);

    // Filter testimonials based on search text and status
    useEffect(() => {
        let filtered = testimonials;

        // Filter by status
        if (filterStatus === 'active') {
            filtered = filtered.filter(t => t.isActive);
        } else if (filterStatus === 'inactive') {
            filtered = filtered.filter(t => !t.isActive);
        }

        // Filter by search text
        if (searchText) {
            const q = searchText.toLowerCase();
            filtered = filtered.filter(t =>
                t.name.toLowerCase().includes(q) ||
                t.title?.toLowerCase().includes(q) ||
                t.review?.toLowerCase().includes(q)
            );
        }

        setFilteredTestimonials(filtered);
    }, [searchText, filterStatus, testimonials]);

    // Fetch all testimonials
    const fetchTestimonials = async () => {
        setLoading(true);
        const token = user?.token || localStorage.getItem('adminToken');

        console.log('=== Fetching Testimonials ===');
        console.log('Token:', token ? 'Present' : 'Missing');
        console.log('API URL:', Utils.API_URL);

        try {
            const res = await getAllTestimonials({ isDeleted: false }, token);
            console.log('Testimonials response:', res);
            console.log('Response type:', typeof res);
            console.log('Response keys:', res ? Object.keys(res) : 'null');

            // Handle different response structures
            if (res?.success && res?.data) {
                // Standard success response with data array
                setTestimonials(res.data || []);
                setFilteredTestimonials(res.data || []);
            } else if (res?.testimonials) {
                // Alternative: data in testimonials field
                setTestimonials(res.testimonials || []);
                setFilteredTestimonials(res.testimonials || []);
            } else if (Array.isArray(res)) {
                // Alternative: direct array response
                setTestimonials(res);
                setFilteredTestimonials(res);
            } else {
                // Error or unexpected format
                console.error('Unexpected response format:', res);
                toast.error(res?.message || "Failed to fetch testimonials");
                setTestimonials([]);
                setFilteredTestimonials([]);
            }
        } catch (err) {
            console.error('Error fetching testimonials:', err);
            toast.error(`Failed to load testimonials: ${err.message || 'Network error'}`);
            setTestimonials([]);
            setFilteredTestimonials([]);
        } finally {
            setLoading(false);
        }
    };

    // Open modal for creating new testimonial
    const handleCreate = () => {
        setModalMode('create');
        setCurrentTestimonial(null);
        setFormData({
            name: '',
            rating: 5,
            title: '',
            review: '',
            isVerified: false,
            isActive: true,
            productId: '',
        });
        setProfileImage(null);
        setImagePreview(null);
        setShowModal(true);
    };

    // Open modal for editing testimonial
    const handleEdit = (testimonial) => {
        setModalMode('edit');
        setCurrentTestimonial(testimonial);
        setFormData({
            name: testimonial.name,
            rating: testimonial.rating,
            title: testimonial.title || '',
            review: testimonial.review || '',
            isVerified: testimonial.isVerified,
            isActive: testimonial.isActive,
            productId: getTestimonialProductId(testimonial),
        });
        setProfileImage(null);
        setImagePreview(testimonial.profileImage ? Utils.getImageUrl(testimonial.profileImage) : null);
        setShowModal(true);
    };

    // Handle image selection
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            // Validate file type
            if (!file.type.startsWith('image/')) {
                toast.error('Please select an image file');
                return;
            }
            // Validate file size (max 5MB)
            if (file.size > 5 * 1024 * 1024) {
                toast.error('Image size should be less than 5MB');
                return;
            }
            setProfileImage(file);
            // Create preview
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!formData.name.trim()) {
            toast.error('Name is required');
            return;
        }
        if (!formData.review.trim()) {
            toast.error('Review is required');
            return;
        }
        setSubmitting(true);
        const token = user?.token || localStorage.getItem('adminToken');

        try {
            const data = new FormData();
            data.append('name', formData.name);
            data.append('rating', formData.rating);
            data.append('title', formData.title);
            data.append('review', formData.review);
            data.append('isVerified', formData.isVerified);
            data.append('isActive', formData.isActive);

            if (profileImage) {
                data.append('profileImage', profileImage);
            }
            if (modalMode === 'edit') {
                data.append('productId', formData.productId || '');
            } else if (formData.productId) {
                data.append('productId', formData.productId);
            }

            let res;
            if (modalMode === 'create') {
                res = await createTestimonial(data, token);
            } else {
                res = await updateTestimonial(currentTestimonial._id, data, token);
            }

            if (res?.success) {
                toast.success(res.message || `Testimonial ${modalMode === 'create' ? 'created' : 'updated'} successfully!`);
                setShowModal(false);
                fetchTestimonials();
            } else {
                toast.error(res?.message || `Failed to ${modalMode} testimonial`);
            }
        } catch (err) {
            console.error(`Error ${modalMode} testimonial:`, err);
            toast.error(`Failed to ${modalMode} testimonial`);
        } finally {
            setSubmitting(false);
        }
    };

    // Toggle testimonial status
    const handleToggleStatus = async (id) => {
        const token = user?.token || localStorage.getItem('adminToken');

        try {
            const res = await toggleTestimonialStatus(id, token);

            if (res?.success) {
                toast.success(res.message || 'Status updated successfully!');
                fetchTestimonials();
            } else {
                toast.error(res?.message || 'Failed to update status');
            }
        } catch (err) {
            console.error('Error toggling status:', err);
            toast.error('Failed to update status');
        }
    };

    // Delete testimonial
    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this testimonial?')) {
            return;
        }

        const token = user?.token || localStorage.getItem('adminToken');

        try {
            const res = await deleteTestimonial(id, token);

            if (res?.success) {
                toast.success(res.message || 'Testimonial deleted successfully!');
                fetchTestimonials();
            } else {
                toast.error(res?.message || 'Failed to delete testimonial');
            }
        } catch (err) {
            console.error('Error deleting testimonial:', err);
            toast.error('Failed to delete testimonial');
        }
    };

    // Export to CSV
    const handleExportCSV = () => {
        if (filteredTestimonials.length === 0) {
            toast.warning("No testimonials to export");
            return;
        }

        const csvContent = exportTestimonialsToCSV(filteredTestimonials);
        if (csvContent) {
            downloadCSV(csvContent, `testimonials_${new Date().toISOString().split('T')[0]}.csv`);
            toast.success("Testimonials exported successfully!");
        }
    };

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                <div className="d-md-flex align-items-center justify-content-between mb-4">
                    <div>
                        <ol className="breadcrumb fs-sm mb-1">
                            <li className="breadcrumb-item"><a href="#">Dashboard</a></li>
                            <li className="breadcrumb-item active" aria-current="page">Homepage testimonials</li>
                        </ol>
                        <h4 className="main-title mb-0">Homepage testimonials</h4>
                        <p className="text-muted small mb-0 mt-1">
                            Homepage testimonial (not product review) — for the marketing homepage carousel only.
                            To add reviews on a product page, use <strong>Product Reviews</strong> in the sidebar.
                        </p>
                    </div>
                    <div className="d-flex gap-2 mt-3 mt-md-0">
                        <Button
                            variant="primary"
                            onClick={handleCreate}
                        >
                            <i className="fa fa-plus"></i> Add homepage testimonial
                        </Button>
                        <Button
                            variant="success"
                            onClick={handleExportCSV}
                            disabled={loading || filteredTestimonials.length === 0}
                        >
                            <i className="fa fa-download"></i> Export CSV
                        </Button>
                    </div>
                </div>

                <Row>
                    <Col xl={12}>
                        <Card>
                            <CardBody>
                                <div className="mb-3">
                                    <Row>
                                        <Col md={6}>
                                            <Form.Control
                                                type="text"
                                                placeholder="Search by name, title, or review..."
                                                value={searchText}
                                                disabled={loading}
                                                onChange={(e) => setSearchText(e.target.value)}
                                            />
                                        </Col>
                                        <Col md={3}>
                                            <Form.Select
                                                value={filterStatus}
                                                disabled={loading}
                                                onChange={(e) => setFilterStatus(e.target.value)}
                                            >
                                                <option value="all">All Status</option>
                                                <option value="active">Active Only</option>
                                                <option value="inactive">Inactive Only</option>
                                            </Form.Select>
                                        </Col>
                                        <Col md={3} className="text-end">
                                            <Button
                                                variant="outline-secondary"
                                                disabled={loading}
                                                onClick={() => {
                                                    setSearchText('');
                                                    setFilterStatus('all');
                                                }}
                                            >
                                                Clear Filters
                                            </Button>
                                        </Col>
                                    </Row>
                                </div>

                                <DataTable
                                    progressPending={loading}
                                    progressComponent={<DataTableSkeleton rows={10} columns={9} />}
                                    columns={columns}
                                    data={filteredTestimonials}
                                    pagination
                                    paginationPerPage={10}
                                    paginationRowsPerPageOptions={[10, 20, 30, 50]}
                                    paginationComponentOptions={dataTablePaginationOptions}
                                    customStyles={dataTableCustomStyles}
                                    highlightOnHover
                                    responsive
                                    striped
                                    noDataComponent={
                                        <AdminEmptyState
                                            icon="ri-chat-quote-line"
                                            title="No testimonials found"
                                            description="Add a testimonial or adjust your search and filters."
                                        />
                                    }
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Create/Edit Modal */}
                <Modal isOpen={showModal} toggle={() => setShowModal(false)} size="lg">
                    <ModalHeader toggle={() => setShowModal(false)}>
                        {modalMode === 'create'
                            ? 'Add homepage testimonial (not product review)'
                            : 'Edit homepage testimonial'}
                    </ModalHeader>
                    <Form onSubmit={handleSubmit}>
                        <ModalBody>
                            <p className="text-muted small">
                                Homepage testimonial (not product review). These appear on the homepage, not on individual product pages.
                            </p>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Enter customer name"
                                            value={formData.name}
                                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                            required
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Rating <span className="text-danger">*</span></Form.Label>
                                        <Form.Select
                                            value={formData.rating}
                                            onChange={(e) => setFormData({ ...formData, rating: parseInt(e.target.value) })}
                                            required
                                        >
                                            <option value="5">⭐⭐⭐⭐⭐ (5 Stars)</option>
                                            <option value="4">⭐⭐⭐⭐ (4 Stars)</option>
                                            <option value="3">⭐⭐⭐ (3 Stars)</option>
                                            <option value="2">⭐⭐ (2 Stars)</option>
                                            <option value="1">⭐ (1 Star)</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>

                            <Form.Group className="mb-3">
                                <Form.Label>Profile Image</Form.Label>
                                <Form.Control
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                />
                                {imagePreview && (
                                    <div className="mt-2">
                                        <img
                                            src={imagePreview}
                                            alt="Preview"
                                            style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '50%' }}
                                        />
                                    </div>
                                )}
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Linked product (optional)</Form.Label>
                                <Form.Select
                                    value={formData.productId}
                                    disabled={productsLoading}
                                    onChange={(e) => setFormData({ ...formData, productId: e.target.value })}
                                >
                                    <option value="">No product linked</option>
                                    {products.map((product) => (
                                        <option key={product._id} value={product._id}>
                                            {product.name}
                                        </option>
                                    ))}
                                </Form.Select>
                                <Form.Text className="text-muted">
                                    Links this homepage testimonial to a catalog product for the storefront.
                                </Form.Text>
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Title</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="e.g., Best Product Ever!"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3">
                                <Form.Label>Review <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    placeholder="Enter homepage testimonial text..."
                                    value={formData.review}
                                    onChange={(e) => setFormData({ ...formData, review: e.target.value })}
                                    required
                                />
                            </Form.Group>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="checkbox"
                                            label="Verified Purchase"
                                            checked={formData.isVerified}
                                            onChange={(e) => setFormData({ ...formData, isVerified: e.target.checked })}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="checkbox"
                                            label="Active (Show on website)"
                                            checked={formData.isActive}
                                            onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="secondary" onClick={() => setShowModal(false)} disabled={submitting}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit" disabled={submitting}>
                                {submitting ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        {modalMode === 'create' ? 'Creating...' : 'Updating...'}
                                    </>
                                ) : (
                                    modalMode === 'create' ? 'Save homepage testimonial' : 'Update homepage testimonial'
                                )}
                            </Button>
                        </ModalFooter>
                    </Form>
                </Modal>

                <Footer />
            </div>
        </React.Fragment>
    );
}

export default Testimonial;
